class AnimatedMetrics extends HTMLElement {
  constructor() {
    super();
    this.metricValues = [];
    this.observer = null;
    this.frameIds = [];
    this.timerIds = [];
    this.started = false;
    this.numberFormatter = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
  }

  connectedCallback() {
    this.metricValues = Array.from(this.querySelectorAll('[data-animated-metric-value]'));

    if (!this.metricValues.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setFinalValues();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.start();
          }
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    } else {
      this.start();
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }

  start() {
    if (this.started) return;

    this.started = true;

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.metricValues.forEach((element) => {
      const settings = this.getMetricSettings(element);
      element.textContent = this.formatValue(settings.start, settings);

      if (settings.target <= settings.start) {
        element.textContent = settings.finalValue;
        return;
      }

      if (settings.animationStyle === 'live') {
        this.animateLive(element, settings);
      } else {
        this.animateSmooth(element, settings);
      }
    });
  }

  getMetricSettings(element) {
    const target = this.parseNumber(element.dataset.target);
    const start = this.parseNumber(element.dataset.start);
    const finalValue = element.dataset.finalValue || '';
    const duration = Number(element.dataset.duration) || 2600;
    const suffixMatch = finalValue.match(/[^\d\s,.]+$/);

    return {
      target,
      start,
      finalValue,
      duration,
      animationStyle: element.dataset.animationStyle,
      suffix: suffixMatch ? suffixMatch[0] : ''
    };
  }

  parseNumber(value) {
    const number = Number(String(value || '').replace(/[^\d-]/g, ''));
    return Number.isFinite(number) ? Math.round(number) : 0;
  }

  formatValue(value, settings) {
    return `${this.numberFormatter.format(Math.round(value))}${settings.suffix}`;
  }

  easeOutCubic(progress) {
    return 1 - Math.pow(1 - progress, 3);
  }

  animateSmooth(element, settings) {
    const startedAt = performance.now();
    const distance = settings.target - settings.start;

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / settings.duration, 1);
      const value = settings.start + distance * this.easeOutCubic(progress);

      element.textContent = progress === 1 ? settings.finalValue : this.formatValue(value, settings);

      if (progress < 1) {
        this.frameIds.push(requestAnimationFrame(tick));
      }
    };

    this.frameIds.push(requestAnimationFrame(tick));
  }

  animateLive(element, settings) {
    const startedAt = performance.now();
    const distance = settings.target - settings.start;
    let current = settings.start;

    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const progress = Math.min(elapsed / settings.duration, 1);

      if (progress >= 1) {
        element.textContent = settings.finalValue;
        return;
      }

      const baseline = settings.start + distance * this.easeOutCubic(progress);
      const canDip = current > settings.start && Math.random() < 0.18;
      const stepScale = Math.max(distance * 0.018, 1);
      const upwardStep = Math.max((baseline - current) * 0.55, stepScale * (0.4 + Math.random()));
      const dipStep = stepScale * (0.12 + Math.random() * 0.35);

      current = canDip ? current - dipStep : current + upwardStep;
      current = Math.max(settings.start, Math.min(current, settings.target));

      if (current >= settings.target) {
        element.textContent = settings.finalValue;
        return;
      }

      element.textContent = this.formatValue(current, settings);

      const pause = Math.random() < 0.14 ? 220 + Math.random() * 360 : 0;
      const timerId = setTimeout(tick, 70 + Math.random() * 110 + pause);
      this.timerIds.push(timerId);
    };

    tick();
  }

  setFinalValues() {
    this.metricValues.forEach((element) => {
      element.textContent = element.dataset.finalValue || element.textContent;
    });
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.frameIds.forEach((frameId) => cancelAnimationFrame(frameId));
    this.timerIds.forEach((timerId) => clearTimeout(timerId));
    this.frameIds = [];
    this.timerIds = [];
  }
}

if (!customElements.get('animated-metrics')) {
  customElements.define('animated-metrics', AnimatedMetrics);
}